export const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export const getCustomerDisplayName = (customer) => {
  if (!customer) return "Sin identificar";
  if (!customer.isCommunityMember) return "Consumidor final";
  return `${customer.firstName} ${customer.lastName}`;
};

export const isProductEligibleForVoucher = (product, voucher) => {
  if (!voucher) return false;
  if (voucher.type === "MONTO") return true;
  if (voucher.type === "PRODUCTO") return Number(product?.id) === Number(voucher.productId);
  if (voucher.type === "CATEGORIA") return product?.category === voucher.category;
  return false;
};

export const getVoucherEligibleQuantity = (cartItems, voucher) =>
  (cartItems || [])
    .filter((item) => isProductEligibleForVoucher(item, voucher))
    .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

export const getStockRestante = (stockTeorico, cantidadSeleccionada = 0) => {
  if (stockTeorico === null || stockTeorico === undefined || stockTeorico === "") return null;
  const stock = Number(stockTeorico);
  if (!Number.isFinite(stock)) return null;
  return Math.max(Math.floor(stock) - Number(cantidadSeleccionada || 0), 0);
};

export const canAddProductByStock = (stockTeorico, cantidadSeleccionada = 0) => {
  const restante = getStockRestante(stockTeorico, cantidadSeleccionada);
  return restante !== null && restante > 0;
};

export const getStockInsuficienteInfo = (error) => {
  const erroresGraphql = error?.graphQLErrors || error?.errors || [];
  const errorStock = erroresGraphql.find(
    (item) => item?.code === "STOCK_INSUFICIENTE" || item?.extensions?.code === "STOCK_INSUFICIENTE"
  );
  const data = errorStock?.data || errorStock?.extensions?.data || error?.data;
  if (!errorStock && data?.tipo !== "STOCK_INSUFICIENTE") return null;

  const faltantes = (data?.resumenStock || data?.deficits || []).map((item) => ({
    idMateriaPrima: Number(item.idMateriaPrima),
    materiaPrima: item.materiaPrima || `Materia prima ${item.idMateriaPrima}`,
    disponible: Number(item.disponible || 0),
    requerido: Number(item.requerido || 0),
    faltante: Number(item.faltante ?? Math.max(Number(item.requerido || 0) - Number(item.disponible || 0), 0)),
    productos: (item.productos || []).map((producto) => ({
      idProducto: Number(producto.idProducto),
      producto: producto.producto || `Producto ${producto.idProducto}`,
      cantidadSolicitada: Number(producto.cantidadSolicitada || 0),
    })),
  }));

  return {
    mensaje: errorStock?.message || error?.message || "No hay stock suficiente para completar la venta.",
    faltantes,
  };
};

export const getVoucherCoverage = ({ cartItems, voucher, selectionOrder = [] }) => {
  const items = cartItems || [];
  const eligibleUnits = getVoucherEligibleQuantity(items, voucher);
  const availableUnits = getVoucherAvailableValue(voucher, voucher?.units);
  const unitLimit = voucher?.wholePaper && eligibleUnits < availableUnits ? 0 : availableUnits;
  const isSpeciesVoucher = ["PRODUCTO", "CATEGORIA"].includes(voucher?.type);

  if (!isSpeciesVoucher) {
    return {
      eligibleUnits,
      coveredUnits: 0,
      unitLimit,
      coveredByProduct: {},
      coveredSubtotal: 0,
    };
  }

  const itemById = Object.fromEntries(items.map((item) => [String(item.id), item]));
  const remainingByProduct = Object.fromEntries(
    items.map((item) => [String(item.id), Number(item.quantity || 0)])
  );
  const fallbackOrder = items.flatMap((item) =>
    Array.from({ length: Number(item.quantity || 0) }, () => item.id)
  );
  const selectedOrder = [...selectionOrder, ...fallbackOrder];
  // El POS envia una linea por producto, ordenada por su primera incorporacion.
  // Sus unidades se cubren juntas, igual que los detalles que evalua la API.
  const orderedProductIds = voucher?.wholePaper
    ? [...new Set(selectedOrder.map(String))].flatMap((key) =>
      Array.from({ length: Number(itemById[key]?.quantity || 0) }, () => key))
    : selectedOrder;
  const coveredByProduct = {};
  let coveredUnits = 0;
  let coveredSubtotal = 0;

  orderedProductIds.forEach((productId) => {
    const key = String(productId);
    const item = itemById[key];
    if (!item || remainingByProduct[key] <= 0) return;

    remainingByProduct[key] -= 1;
    if (coveredUnits >= unitLimit || !isProductEligibleForVoucher(item, voucher)) return;

    coveredByProduct[key] = (coveredByProduct[key] || 0) + 1;
    coveredUnits += 1;
    coveredSubtotal += Number(item.price || 0);
  });

  return {
    eligibleUnits,
    coveredUnits,
    unitLimit,
    coveredByProduct,
    coveredSubtotal,
  };
};

// Los fisicos reales requieren saldo para la denominacion completa. El
// indicador no se aplica a los ejemplos demo que conservan su calculo previo.
export const getVoucherAvailableValue = (voucher, value) => {
  const paper = Number(value || 0);
  const available = Math.max(0, Math.min(paper,
    voucher?.saldo == null ? Infinity : Number(voucher.saldo),
    voucher?.saldoDiario == null ? Infinity : Number(voucher.saldoDiario)));
  return voucher?.wholePaper && available + 0.000001 < paper ? 0 : available;
};

// No calcula IVA ni condición fiscal: el desglose impositivo lo resuelve el
// backend al cotizar y facturar. La pantalla sólo suma precio por cantidad.
export const calculateSaleTotals = ({ cartItems, voucher, selectionOrder = [], porcentajeDescuentoEvento = 0, menuAplicaDescuento = true, comunidad = false }) => {
  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const coverage = getVoucherCoverage({ cartItems, voucher, selectionOrder });
  const baseEvento = cartItems.reduce((sum, item) => sum + (item.esMenuUniversitario && !menuAplicaDescuento ? 0 : Math.round(Number(item.price) * Math.max(0, Number(item.quantity) - Number(coverage.coveredByProduct[item.id] || 0)) * 100)), 0) / 100;
  const descuentoEventoMasivo = comunidad ? Math.round(baseEvento * Number(porcentajeDescuentoEvento || 0)) / 100 : 0;
  const saldoEvento = Math.max(0, Math.round((total - descuentoEventoMasivo) * 100) / 100);
  const voucherCanApply = !voucher || voucher.type === "MONTO"
    ? total > 0 && (!voucher || (getVoucherAvailableValue(voucher, voucher.amount) > 0
      && (!voucher.wholePaper || saldoEvento + 0.000001 >= Number(voucher.amount))))
    : coverage.coveredUnits > 0;
  const eligibleSubtotal = (cartItems || [])
    .filter((item) => isProductEligibleForVoucher(item, voucher))
    .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const voucherApplied = !voucher || !voucherCanApply
    ? 0
    : voucher.type === "MONTO"
      ? Math.min(getVoucherAvailableValue(voucher, voucher.amount), saldoEvento)
      : coverage.coveredSubtotal;

  return {
    total,
    eligibleSubtotal,
    voucherEligibleUnits: coverage.eligibleUnits,
    voucherCoveredUnits: coverage.coveredUnits,
    voucherUnitLimit: coverage.unitLimit,
    voucherCoverageByProduct: coverage.coveredByProduct,
    voucherCanApply,
    voucherError: voucher?.wholePaper && !voucherCanApply
      ? getVoucherAvailableValue(voucher, voucher.units ?? voucher.amount) <= 0
        ? "El saldo total o diario no alcanza para un papel completo. Elegí otra tanda o quitá el voucher."
        : voucher.type === "MONTO"
          ? `El pedido debe alcanzar ${formatCurrency(voucher.amount)} para utilizar este papel completo.`
          : `Este papel requiere ${voucher.units} unidades del producto o categoría incluidos. Completá el pedido o quitá el voucher.`
      : "",
    voucherApplied,
    descuentoEventoMasivo,
    descuentoVoucher: voucherApplied,
    baseDescuentoEvento: baseEvento,
    porcentajeDescuentoEvento: comunidad ? porcentajeDescuentoEvento : 0,
    importeIva: comunidad ? 0 : Math.round((saldoEvento - voucherApplied - Math.round((saldoEvento - voucherApplied) / 1.21 * 100) / 100) * 100) / 100,
    importeNeto: comunidad ? saldoEvento - voucherApplied : Math.round((saldoEvento - voucherApplied) / 1.21 * 100) / 100,
    amountDue: Math.max(Math.round((saldoEvento - voucherApplied) * 100) / 100, 0),
  };
};
